import type React from 'react';
import { useCallback, useMemo } from 'react';

import SliderTrack from './components/SliderTrack';
import TimeDisplay from './components/TimeDisplay';
import TimeMarks from './components/TimeMarks';
import type { TimeRangeSliderProps } from './types';
import { calculateTimeRange, formatTimeRange, generateTimeMarks, minutesToTime } from './utils';

const TimeRangeSlider: React.FC<TimeRangeSliderProps> = ({ startTime, endTime, minTime, maxTime, onChange, className = '' }) => {
  const timeRange = useMemo(() => {
    return calculateTimeRange(startTime, endTime, minTime, maxTime);
  }, [startTime, endTime, minTime, maxTime]);

  const timeMarks = useMemo(() => {
    return generateTimeMarks(timeRange);
  }, [timeRange]);

  const formattedTimeRange = useMemo(() => {
    return formatTimeRange(timeRange.startMinutes, timeRange.endMinutes);
  }, [timeRange.startMinutes, timeRange.endMinutes]);

  const handleSliderChange = useCallback(
    (values: number[]) => {
      const [start, end] = values;
      const newStartTime = minutesToTime(start);
      const newEndTime = minutesToTime(end);
      onChange(newStartTime, newEndTime);
    },
    [onChange]
  );

  return (
    <div className={`relative w-full ${className}`}>
      <TimeDisplay startTime={startTime} endTime={endTime} timeRange={formattedTimeRange} />

      <SliderTrack
        startMinutes={timeRange.startMinutes}
        endMinutes={timeRange.endMinutes}
        minMinutes={timeRange.minMinutes}
        maxMinutes={timeRange.maxMinutes}
        onValueChange={handleSliderChange}
      />

      <div className='relative'>
        <TimeMarks timeMarks={timeMarks} />
      </div>
    </div>
  );
};

export default TimeRangeSlider;
